import React from "react";
import {CcRecipientDTO} from "../../../../../../api/generated";
import {RootState} from "../../../../../../app/store";
import {ValidationComponent, OnValidChangeHandler} from "../../../../../elements/validator/component/ValidationComponent";
import {ERRORS_HOLDER, NotEmptyStringValidator, OptionalEmailValidator, ValueValidator} from "../../../../../elements/validator/Validator";
import styles from "../BotForm.module.css";
import type {BotRequest, BotRequestPartial} from "../BotForm";
import {ValidatedInputField} from "../../../../../elements/validator/component/ValidatedInputField";

interface Props<T extends BotRequest> {
    selector: (state: RootState) => CcRecipientDTO[] | undefined;
    updateRequest: (update: BotRequestPartial) => void;
    updateValidationStatus: OnValidChangeHandler<T>;
    getMessage: (fieldName: string) => string | undefined;
}

const ccRecipientsValidator: ValueValidator<CcRecipientDTO[]> = async (fieldName, value) => {
    const recipients = value || [];
    const emails = recipients
        .map(recipient => recipient.email?.trim().toLowerCase() || "")
        .filter(Boolean);

    const uniqueEmails = new Set(emails);
    if (emails.length !== uniqueEmails.size) {
        return {
            valid: false,
            message: `${fieldName} contains duplicate email addresses`
        };
    }

    return ERRORS_HOLDER.VALID;
}

const requiredEmailValidator: ValueValidator<string | undefined> = async (fieldName, value) => {
    const notEmptyValidation = await NotEmptyStringValidator(fieldName, value);
    if (!notEmptyValidation.valid) {
        return notEmptyValidation;
    }

    return OptionalEmailValidator(fieldName, value?.trim());
}

export const CcRecipientsField = <T extends BotRequest, >({
                                           selector,
                                           updateRequest,
                                           updateValidationStatus,
                                           getMessage
                                       }: Props<T>) => {
    const updateRecipients = (ccRecipients: CcRecipientDTO[]) => {
        updateRequest({ccRecipients});
    };

    const updateRecipient = (
        recipients: CcRecipientDTO[],
        index: number,
        update: Partial<CcRecipientDTO>
    ) => {
        updateRecipients(recipients.map((recipient, i) => i === index ? {...recipient, ...update} : recipient));
    };

    const removeRecipient = (recipients: CcRecipientDTO[], index: number) => {
        updateRecipients(recipients.filter((_, i) => i !== index));
    };

    const fieldSelector = (index: number, field: keyof CcRecipientDTO) => (state: RootState): string | undefined => {
        return selector(state)?.[index]?.[field];
    };

    const fieldName = (index: number, field: keyof CcRecipientDTO) => {
        return `ccRecipients.${index}.${field}`;
    };

    return <ValidationComponent<CcRecipientDTO[], T>
        selector={selector}
        fieldName={"ccRecipients"}
        label={"CC Recipient(s)"}
        onChange={updateRecipients}
        updateValidationStatus={updateValidationStatus}
        validator={ccRecipientsValidator}
        required={false}
        child={(label, value) => {
            const recipients = value || [];
            return <div className={styles.ccRecipientsWrapper}>
                <div className={styles.ccRecipientsHeader}>
                    <div>
                        <div className={styles.titleWrapper}>
                            <span className={styles.title}>{label}</span>
                        </div>
                        <div className={styles.ccRecipientsDescription}>
                            These contacts are added as CC recipients for assistant emails.
                        </div>
                    </div>
                    <button
                        type={"button"}
                        className={styles.ccRecipientAddButton}
                        onClick={() => updateRecipients([...recipients, {title: "", email: ""}])}
                    >
                        <img src={"/plusIcon.svg"} alt={"Add recipient"}/>
                        <span>Add recipient</span>
                    </button>
                </div>
                {getMessage("ccRecipients") && (
                    <div className={styles.ccRecipientsError}>
                        <img src={"/alertIcon.svg"} alt={"alert icon"}/>
                        <span>{getMessage("ccRecipients")}</span>
                    </div>
                )}
                <div className={styles.ccRecipientsList}>
                    {!recipients.length && (
                        <div className={styles.ccRecipientsEmptyState}>
                            No CC recipients configured.
                        </div>
                    )}
                    {recipients.map((recipient, index) => {
                        const titleFieldName = fieldName(index, "title");
                        const emailFieldName = fieldName(index, "email");

                        return <div className={styles.ccRecipientRow} key={`cc-recipient-${index}`}>
                            <ValidatedInputField<string | undefined, T>
                                validator={NotEmptyStringValidator}
                                selector={fieldSelector(index, "title")}
                                fieldName={titleFieldName}
                                onChange={(title) => updateRecipient(recipients, index, {title})}
                                updateValidationStatus={updateValidationStatus}
                                label={"Title"}
                                placeholder={"Marketing snows"}
                                getMessage={() => getMessage(titleFieldName) || ""}
                            />
                            <ValidatedInputField<string | undefined, T>
                                validator={requiredEmailValidator}
                                selector={fieldSelector(index, "email")}
                                fieldName={emailFieldName}
                                onChange={(email) => updateRecipient(recipients, index, {email})}
                                updateValidationStatus={updateValidationStatus}
                                label={"Email"}
                                placeholder={"example@mail.com"}
                                getMessage={() => getMessage(emailFieldName) || ""}
                            />
                            <button
                                type={"button"}
                                className={styles.ccRecipientIconButton}
                                onClick={() => removeRecipient(recipients, index)}
                                title={"Remove recipient"}
                            >
                                <img src={"/deleteIcon.svg"} alt={"Remove recipient"}/>
                            </button>
                        </div>
                    })}
                </div>
            </div>
        }}
    />
}
